import { ref, computed, onMounted } from 'vue';
import { f7, theme } from 'framework7-vue';
import { openAdaptiveModal } from './useAdaptiveModal.js';
import _ from 'lodash';
import i18n from '@/js/i18n.js';
import { Clipboard } from '@capacitor/clipboard';
import { useAuthStore } from '../pinia-stores/auth.js';
import { useAccountStore } from '../pinia-stores/account.js';
import { useMessageStore } from '../pinia-stores/message.js';
import { useDesktopScreen, useTabletScreen } from './useMediaQuery.js';
import { useGroupCreation } from './useGroupCreation.js';

export function useMessageOptions(options = {}) {
  const {
    isTablet,
    profile,
    userAccount,
    messageSocketClient,
    reactionEmoji,
    selectedMessages,
    messageIsSelected,
    currentMessage,
    messageInScope,
    transferMsgRecipients,
    chatUser,
    groupList,
    group,
    messageList,
    reactionMessage,
    isOpenedReactionPopover,
    isGroupAdmin,
  } = options;

  const messageActionSheet = ref(null);
  const groupActionSheet = ref(null);
  const toastIcon = ref(null);
  // Guards the transfer panel against a second send while the first one is
  // still going out.
  const transferring = ref(false);

  function reducePageWidth() {
    if (isTablet?.value === true) {
      f7.$(
        '.view-master-detail .page-master-detail, .view-master-detail .navbar-master-detail'
      ).css({
        width: 'calc(100% - 2*var(--f7-page-master-width))',
      });
    }
  }

  function resetPageWidth() {
    if (isTablet?.value === true) {
      f7.$(
        '.view-master-detail .page-master-detail, .view-master-detail .navbar-master-detail'
      ).css({
        width: 'calc(100% - var(--f7-page-master-width))',
      });
    }
  }

  function addParticipantGroupRightPanel() {
    f7.panel.open('.addParticipantGroupRightPanel');
  }

  function openSendMessageLeftPanel(users) {
    if (users && users.length > 0) {
      getUserList();
    }
    f7.panel.toggle('.sendMessageLeftPanel', true);
  }

  function attachFileOpenActions() {
    f7.actions.open('.attachFileForMobile');
  }

  function opencreateGroupLeftPanel() {
    useGroupCreation().openGroupCreation();
  }

  function openContactDetailPanel() {
    f7.panel.toggle('.contactDetailPanel', true);
  }

  function openMsgActionsSheet(message) {
    currentMessage.value = message;
    f7.sheet.open('.messageActionsSheet');
  }

  function openMsgActionsPopover(message) {
    currentMessage.value = message;
  }

  function openMessageOpenActions(message) {
    if (f7.actions?.opened) {
      messageActionSheet.value?.close();
    }
    currentMessage.value = message;
    const emojList = (reactionEmoji?.value || []).map((em) => {
      const reaction = message?.reactions && Array.isArray(message.reactions)
        ? message.reactions.find((r) => r.emojiString === em.id)
        : null;
      const userReacted =
        reaction &&
        Array.isArray(reaction.authors) &&
        reaction.authors.some((a) => a.userId === userAccount?.value?.userId);
      const activeStyle = userReacted
        ? 'backgroud-color:var(--f7-menu-list-item-bg-color); border-radius:25%'
        : 'box-shadow: none;';
      const activeClass = userReacted ? 'user-reacted' : '';
      return `<button style="padding:0;margin-left:8px;color:var(--f7-theme-color); font-size:24px;${activeStyle}" class="button emoji-btn link likeAction ${em.id === '+1' ? 'thumbsup' : ''} ${activeClass}" data-emoji-id="${em.id || ''}" data-emoji-native="${em.native || ''}" aria-label="${em.id || ''}">${em.native || em.id}</button>`;
    }).join('');

    const emojtext = `<div class="cols90_10 grid-rows-1  interactionsIcons margin-vertical-half"
      style="overflow: hidden">
      <span class="grid-rows-1 display-flex margin-right-half" style="
          overflow-x: auto;
          overflow-y: hidden;
          -ms-overflow-style: none;
          scrollbar-width: none;
          flex-direction: row;
        ">
        ${emojList}
      </span>
      <a  class="link open-emoji-dialog margin-right-half" aria-label="add-emoji"> <i class="f7-icons if-not-md  size-24">plus_circle</i><i class="material-icons md-only size-24">add_circle_outline</i></a>
    </div>`;

    const buttonsList = [];
    // if (isTablet?.value) {
      buttonsList.push(
        {
          text: i18n.global.t('tamtammessage.selectMessage'),
          icon: `<i class="f7-icons if-not-md color-deeppurple">square_list</i><i class="material-icons md-only color-deeppurple">playlist_add_check</i>`,
          color: 'deeppurple',
          strong: true,
          onClick() {
            selectMessage(message);
          },
        },
        {
          text: i18n.global.t('tamtammessage.copyMessage'),
          icon: `<i class="f7-icons if-not-md color-lime">square_on_square</i><i class="material-icons md-only color-lime">content_copy</i>`,
          color: 'lime',
          strong: true,
          onClick() {
            copyMsg(message);
          },
        }
      );
      if (message.senderId === profile?.value?.sub || isGroupAdmin) {
        buttonsList.unshift({
          text: i18n.global.t('tamtammessage.deleteMessageForAll'),
          icon: `<i class="f7-icons if-not-md color-red">trash_slash</i><i class="material-icons md-only color-red">delete_forever</i>`,
          color: 'red',
          strong: true,
          onClick() {
            deleteForAll(message);
          },
        });
      }
      
      // Edit button
      if (message.senderId === profile?.value?.sub) {
        buttonsList.unshift({
          text: i18n.global.t('tamtammessage.editMessage'),
          icon: `<i class="f7-icons if-not-md color-blue">pencil</i><i class="material-icons md-only color-blue">edit</i>`,
          color: 'blue',
          strong: true,
          onClick() {
            if (messageActionSheet.value) {
              messageActionSheet.value.close();
            }
            if (typeof options.onEditMessage === 'function') {
              options.onEditMessage(message);
            }
          },
        });
      }
      buttonsList.unshift(
        {
          text: i18n.global.t('tamtammessage.forwardMessage'),
          icon: `<i class="f7-icons if-not-md color-gray">arrowshape_turn_up_right</i><i class="material-icons md-only color-gray" style="transform: scaleX(-1)">reply</i>`,
          color: 'gray',
          strong: true,
          onClick() {
            forwardMsg(message);
          },
        },
        {
          text: i18n.global.t('tamtammessage.deleteMessageForMe'),
          icon: `<i class="f7-icons if-not-md color-orange">trash</i><i class="material-icons md-only color-orange">delete</i>`,
          color: 'deeporange',
          strong: true,
          onClick() {
            deleteForMe(message);
          },
        }
      );
      if (
        message.groupType !== 'PERSONAL' &&
        message.senderId === profile?.value?.sub
      ) {
        buttonsList.unshift({
          text: i18n.global.t('tamtammessage.replyPrivately'),
          icon: `<i class="f7-icons if-not-md color-teal">arrowshape_turn_up_left</i><i class="material-icons md-only color-teal">reply</i>`,
          color: 'teal',
          strong: true,
          onClick() {
            replyMsgPrivately(message);
          },
        });
      }
      if (message.groupType !== 'PERSONAL') {
        buttonsList.unshift(
          {
            text: i18n.global.t('tamtammessage.reply'),
            icon: `<i class="f7-icons size-50 if-not-md color-primqry">arrowshape_turn_up_left_2</i><i class="material-icons size-50 md-only color-primary">reply_all</i>`,
            color: 'primary',
            strong: true,
            onClick() {
              replyMsg(message);
            },
          },
          {
            text: i18n.global.t('tamtammessage.reply'),
            icon: `<i class="f7-icons if-not-md color-lightblue">arrowshape_turn_up_left</i><i class="material-icons md-only color-lightblue">reply</i>`,
            color: 'lightblue',
            strong: true,
            onClick() {
              replyMsg(message);
            },
          }
        );
      }
    // }
    buttonsList.unshift({
      text: emojtext,
    });

    messageActionSheet.value = f7.actions.create({
      convertToPopover: true,
      closeByOutsideClick: true,
      forceToPopover: true,
      closeOnEscape: true,
      backdrop: true,
      arrow: isTablet?.value ? true : false,
      targetEl: isTablet?.value || !f7.support.touch ? f7.$(`.targetIcon${currentMessage.value.id}`) : f7.$(`.message_${currentMessage.value.id}`),
      buttons: buttonsList,
    });

    messageActionSheet.value.open();

    setTimeout(() => {
      try {
        f7.$('.emoji-btn').off('click').on('click', (e) => {
          const btn = f7.$(e.currentTarget);
          const emojiId = btn.attr('data-emoji-id');
          const emojiObj = (reactionEmoji?.value || []).find((em) => em.id === emojiId);
          if (emojiObj && typeof options.interactionSelect === 'function') {
            options.interactionSelect(emojiObj);
          }
          f7.actions.close(messageActionSheet.value);
        });

        f7.$('.open-emoji-dialog').off('click').on('click', (e) => {
          e.preventDefault();
          if (typeof options.toogleDialogEmoji === 'function') {
            options.toogleDialogEmoji(true);
          }
        });
      } catch (err) {
        console.error('Failed to bind emoji handlers', err);
      }
    }, 50);
  }

  function groupDetailPopover(groupItem) {
    if (f7.popover.opened) {
      groupActionSheet.value.close();
    }

    let isProtected = false;
    if (groupItem.groupParticipantList) {
      isProtected = groupItem.groupParticipantList.find((x) => x.userId === profile?.value?.sub) ? true : false;
    } else if (groupItem.subGroupList) {
      isProtected = groupItem.subGroupList.find((x) => x.userId === profile?.value?.sub) ? true : false;
    }

    const buttonsList = [
      {
        text: i18n.global.t('tamtam.markAsUnread'),
        icon: `<i class="f7-icons if-not-md color-deeppurple">square_list</i><i class="material-icons md-only color-deeppurple">playlist_add_check</i>`,
        color: 'teal',
        strong: true,
        onClick() {
          if (options.markUnread) options.markUnread(groupItem);
        },
      },
      {
        text: isProtected ? i18n.global.t('common.unprotectGroup') : i18n.global.t('common.protectGroup'),
        icon: isProtected ? `<i class="f7-icons if-not-md color-red">lock_open</i><i class="material-icons md-only color-red">lock_open</i>` : `<i class="f7-icons if-not-md color-red">lock</i><i class="material-icons md-only color-red">lock</i>`,
        color: 'red',
        strong: true,
        onClick() {
          if (isProtected) {
            unprotectGroup(groupItem);
          } else {
            protectGroup(groupItem);
          }
        },
      },
    ];
    if (groupItem.groupType === 'PERSONAL') {
      buttonsList.unshift({
        text: i18n.global.t('tamtam.deleteChat'),
        icon: `<i class="f7-icons if-not-md color-red">trash_slash</i><i class="material-icons md-only color-red">delete_forever</i>`,
        color: 'red',
        strong: true,
        onClick() {
          if (options.deleteChat) options.deleteChat(groupItem);
        },
      });
    }
    if (groupItem.groupType === 'GROUP' || groupItem.groupType === 'SUBGROUP') {
      buttonsList.unshift(
        {
          text: i18n.global.t('tamtam.exitGroup'),
          icon: `<i class="f7-icons if-not-md color-orange">square_arrow_right</i><i class="material-icons md-only color-orange" >exit_to_app</i>`,
          color: 'orange',
          strong: true,
          onClick() {
            if (options.leaveGroup) options.leaveGroup(groupItem);
          },
        },
        {
          text:
            groupItem.groupType === 'GROUP'
              ? i18n.global.t('tamtam.deleteGroup')
              : i18n.global.t('tamtam.deleteSubgroup'),
          icon: `<i class="f7-icons if-not-md color-deeporange">trash</i><i class="material-icons md-only color-deeporange">delete</i>`,
          color: 'deeporange',
          strong: true,
          onClick() {
            if (options.deleteGroup) options.deleteGroup(groupItem);
          },
        }
      );
    }

    if (groupItem.groupType === 'GROUP' && groupItem.admin === true) {
      buttonsList.unshift({
        text: i18n.global.t('tamtam.addSubgroup'),
        icon: `<i class="f7-icons size-50 if-not-md color-teal">plus-circle</i><i class="material-icons size-50 md-only color-teal">add_circle_outline</i>`,
        color: 'teal',
        strong: true,
        onClick() {
          if (options.addSubgroup) options.addSubgroup(groupItem);
        },
      });
    }

    groupActionSheet.value = f7.actions.create({
      convertToPopover: true,
      closeByOutsideClick: true,
      closeOnEscape: true,
      backdrop: true,
      targetEl: f7.$(`.targetIcon${groupItem.id}`),
      buttons: buttonsList,
    });

    groupActionSheet.value.open();
  }

  function protectGroup(groupItem) {
    useMessageStore().protectGroup({
      groupId: groupItem.id,
      protectGroup: true,
    });
  }

  function unprotectGroup(groupItem) {
    useMessageStore().protectGroup({
      groupId: groupItem.id,
      protectGroup: false,
    });
  }

  function messageActions(message) {
    // if (isTablet?.value || !f7.support.touch) {
    //   return;
    // }

    messageInScope.value = message;

    if (messageIsSelected?.value) {
      const alreadySelected = (selectedMessages?.value || []).some(
        (m) => m.id === message.id
      );
      
      if (alreadySelected) {
        openMessageOpenActions(message);
        f7.taptic?.impact({ style: 'light' });
      } else {
        selectMessage(message);
        f7.taptic?.impact({ style: 'light' });
      }
      return;
    }

    const userStatus = message.userMessageStatus?.find(
      (status) => status.userId === profile?.value?.sub
    );
    const isSelectable = !userStatus?.status || userStatus.status !== 'deleted';
    
    if (!isSelectable) {
      openMessageOpenActions(message);
      f7.taptic?.impact({ style: 'medium' });
      return;
    }
    
    selectMsg(message);
    f7.taptic?.impact({ style: 'medium' });
  }

  function replyMsg(message) {
    useMessageStore().setReplyMsg(message);
  }

  function removeReplyMessage() {
    useMessageStore().setReplyMsg('');
  }

  function replyMsgPrivately(message) {
    const groupId = group?.value?.id ? group.value.id : message.senderid;
    f7.views.main.router.navigate(`/message/${groupId}/PERSONAL`, {
      reloadAll: true,
    });
  }

  function editMessage(message) {
    useMessageStore().setEditMessage(message);
  }

  function deleteForMe(message) {
    f7.dialog.confirm(i18n.global.t('tamtammessage.deleteMessage'), () => {
      useMessageStore().deleteMessage({
        messageId: message.id,
        forAll: false,
      });
      f7.messages.removeMessage(`.message_${message.id}`);
    });
  }

  function deleteSelectedMsg() {
    f7.dialog.confirm(i18n.global.t('tamtammessage.deleteSelectedMessages'), () => {
      for (const message of selectedMessages?.value || []) {
        useMessageStore().deleteMessage({
          messageId: message.id,
          forAll: false,
        });
        f7.messages.removeMessage(`.message_${message.id}`);
      }
      clearSelection();
    });
  }

  function deleteForAll(message) {
    f7.dialog.confirm(i18n.global.t('tamtammessage.deleteMessageForAll'), () => {
      useMessageStore().deleteMessage({
        messageId: message.id,
        forAll: true,
      });
      f7.messages.removeMessage(`.message_${message.id}`);
    });
  }

  function forwardAllMsg() {
    f7.panel.toggle('.transferMessageContactSelection', true);
  }

  function selectRecipientUser(user) {
    const isTabletVal = isTablet?.value === true;
    const isTouch = f7.support.touch;
    const userIndex = (transferMsgRecipients?.value || []).findIndex(
      (x) => x.id === user.id
    );

    if (userIndex >= 0) {
      if (!isTabletVal || isTouch) {
        f7.$(`.transferMsgRecipient${user.id}`).css({
          'background-color': 'transparent',
        });
      }
      if (isTabletVal || !isTouch) {
        f7.$(`input[name=transferMsg${user.id}]`).prop('checked', false);
      }
      transferMsgRecipients.value.splice(userIndex, 1);
      deleteRecipientChip(user);
      return;
    }

    transferMsgRecipients.value.push(user);
    if (!isTabletVal || isTouch) {
      f7.$(`.transferMsgRecipient${user.id}`).css({
        'background-color': '#87bfff',
      });
    }
    if (isTabletVal || !isTouch) {
      f7.$(`input[name=transferMsg${user.id}]`).prop('checked', true);
    }
  }

  function deleteRecipientChip(recipient) {
    const index = (transferMsgRecipients?.value || []).indexOf(recipient);
    transferMsgRecipients.value.splice(index, 1);
    if (!isTablet?.value || f7.support.touch) {
      f7.$(`.transferMsgRecipient${recipient.id}`).css({
        'background-color': 'transparent',
      });
    }
    if (isTablet?.value || !f7.support.touch) {
      f7.$(`input[name=transferMsg${recipient.id}]`).prop('checked', false);
    }
  }

  /**
   * A forwarded message keeps whatever kind it was: a `feed_card` or a `title`
   * is not a chat bubble, and overwriting `type` made those render as ordinary
   * sent text in the target conversation.
   */
  function transferMessageType(message) {
    const type = String(message?.type || '').toLowerCase();
    return ['title', 'feed_card'].includes(type) ? message.type : 'sent';
  }

  /** Which kind of conversation a picked chip points at. */
  function transferRecipientType(recipient) {
    const groupType = String(recipient?.groupType || '').toUpperCase();
    if (groupType === 'GROUP' || groupType === 'SUBGROUP') return groupType;
    // Sub-group payloads can arrive without a group type but always carry their
    // own lists; without this they fall through to the 1-1 branch and were sent
    // to an undefined receiver.
    if (recipient?.subGroupMessageList || recipient?.subGroupParticipantList) {
      return 'SUBGROUP';
    }
    if (recipient?.userId) return 'PERSONAL';
    return null;
  }

  /** Resolves a chip to a concrete destination, or null when unusable. */
  async function resolveTransferTarget(recipient) {
    const recipientType = transferRecipientType(recipient);
    if (!recipientType) return null;
    if (recipientType === 'PERSONAL') {
      const personal = await useMessageStore().resolvePersonalChatGroup(recipient);
      if (!personal?.chatGroupId) return null;
      return {
        groupType: 'PERSONAL',
        // The chat row id of the contact, not their Keycloak subject: the
        // backend parses `receiverId` as a UUID before it stores anything.
        receiverId: personal.receiverId || recipient.id || recipient.userId,
        receiverName: recipient.name,
        chatGroupId: personal.chatGroupId,
      };
    }
    return {
      groupType: recipientType,
      receiverId: recipient.id,
      receiverName: recipient.name,
      chatGroupId: recipient.id,
    };
  }

  function buildTransferMessage(message, target) {
    const messageStore = useMessageStore();
    return {
      text: message.text || '',
      senderId: messageStore.currentUserId,
      senderName: messageStore.currentSenderName,
      sentAt: new Date().toISOString(),
      readAt: '',
      attachment: '',
      // The reply target lives in the source conversation and is meaningless
      // once the message is forwarded.
      replyingTo: '',
      type: transferMessageType(message),
      status: 'sent',
      msgType: message.msgType || 'text',
      // Forwarded messages carry their own files; they must not collect the
      // composer's pending uploads.
      attachments: Array.isArray(message.attachments) ? message.attachments : [],
      feedCard: message.feedCard,
      groupType: target.groupType,
      receiverId: target.receiverId,
      receiverName: target.receiverName,
      chatGroup: { id: target.chatGroupId },
    };
  }

  async function transferMessages() {
    const messageStore = useMessageStore();
    const recipients = [...(transferMsgRecipients?.value || [])];
    const messages = [...(selectedMessages?.value || [])];
    if (recipients.length === 0 || messages.length === 0) {
      return { sent: [], failed: [] };
    }

    const sent = [];
    const failed = [];
    const targets = [];
    // Claimed before the first await: otherwise a second tap lands in the
    // window where the identity request is still in flight and both run.
    transferring.value = true;
    try {
      // Every outgoing message is stamped with the signed-in user's id, so the
      // chat profile has to be resolved before the first send.
      await messageStore.ensureChatUser();
      for (const recipient of recipients) {
        let target = null;
        try {
          target = await resolveTransferTarget(recipient);
        } catch (err) {
          messageStore.error = err?.response?.data || err.message;
        }
        if (!target) {
          failed.push(recipient);
          continue;
        }
        targets.push(target);
        for (const message of messages) {
          try {
            // A fresh payload per recipient and per message: reusing one object
            // made every recipient of a multi-recipient transfer end up with
            // the last recipient's receiver.
            await messageStore.sendPreparedChatMessage(
              buildTransferMessage(message, target)
            );
            sent.push({ target, message });
          } catch (err) {
            failed.push(recipient);
          }
        }
      }
    } finally {
      transferring.value = false;
    }

    if (sent.length > 0) {
      [...(transferMsgRecipients?.value || [])].forEach((recipient) => {
        deleteRecipientChip(recipient);
      });
      clearSelection();
      useMessageStore().messageIsSelectedAction(false);
      f7.panel.close('.transferMessageContactSelection', true);
    }

    if (failed.length > 0) {
      f7.dialog.alert(
        i18n.global.t('tamtammessage.transferFailed'),
        'Transfer message'
      );
    }

    // With a single destination the caller ppens that conversation: that is the
    // confirmation the sender is looking for. With several there is no single
    // conversation to show, so the caller keeps the user where they are.
    return { sent, failed, target: targets.length === 1 ? targets[0] : null };
  }

  function makeSelectable() {
    useMessageStore().messageIsSelectedAction(true);
    if (isTablet?.value || !f7.support.touch) {
      f7.$('.message').css({ 'margin-left': '100px' });
      f7.$('.multiMessagesSelect').css({ display: 'block' });
    }
  }

  function selectMsg(message) {
    useMessageStore().messageIsSelectedAction(true);
    if (isTablet?.value || !f7.support.touch) {
      f7.$('.message').css({ 'margin-left': '100px' });
    }
    selectMessage(message);
  }

  function selectMessage(message) {
    const userStatus = message.userMessageStatus?.find(
      (status) => status.userId === profile?.value?.sub
    );
    const isSelectable = !userStatus?.status || userStatus.status !== 'deleted';
    if (!isSelectable) return;

    const targetCheckbox = f7.$(`input[name=checkbox${message.id}]`);
    const selectedMsgClass = f7.$(`.selectedMsgClass${message.id}`);
    const selectedIdx = (selectedMessages?.value || []).findIndex((msg) => msg.id === message.id);

    if (selectedIdx >= 0) {
      selectedMsgClass.removeClass('selectedMsg--active');
      if (isTablet?.value || !f7.support.touch) {
        targetCheckbox.prop('checked', false);
        f7.$('.message .message-received').removeClass('marging-left');
        selectedMsgClass.removeClass(
          'margin-vertical-half no-padding-vertical'
        );
      }
      useMessageStore().selectMessage({
        msg: message,
        action: 'remove',
      });
      if (!selectedMessages?.value?.length && messageIsSelected?.value) {
        clearSelection();
      }
      return;
    }

    selectedMsgClass.addClass('selectedMsg--active');
    useMessageStore().selectMessage({
      msg: message,
      action: 'add',
    });
    useMessageStore().messageIsSelectedAction(true);

    if (isTablet?.value || !f7.support.touch) {
      f7.$('.message').css({ 'margin-left': '100px' });
      targetCheckbox.prop('checked', true);
      selectedMsgClass.addClass('margin-vertical-half no-padding-vertical');
      f7.$('.message .message-received').addClass('marging-left');
    }
  }

  function clearSelection() {
    if (Array.isArray(selectedMessages?.value) && selectedMessages.value.length > 0) {
      const ids = selectedMessages.value.map((msg) => msg.id);
      ids.forEach((id) => {
        f7.$(`input[name=checkbox${id}]`).prop('checked', false);
        f7.$(`.selectedMsgClass${id}`).removeClass('margin-vertical-half no-padding-vertical selectedMsg--active');
      });
      useMessageStore().clearSelectedMessages();
    }

    setTimeout(() => {
      f7.$('.message .message-received').removeClass('marging-left');
      f7.$('.selectedMsg').removeAttr('style');
      if (isTablet?.value || !f7.support.touch) {
        f7.$('.message').removeAttr('style');
        f7.$('.multiMessagesSelect').css({ display: 'none' });
      }
      messageInScope.value = '';
      f7.$('.tamtamNav').css({ display: 'block' });
      useMessageStore().messageIsSelectedAction(false);
    }, 50);
  }

  async function copyMsg(message) {
    await Clipboard.write({
      string: message.text,
    });
    if (!toastIcon.value) {
      toastIcon.value = f7.toast.create({
        icon: theme.ios
          ? '<i class="f7-icons color-green">checkmark_alt_circle_fill</i>'
          : '<i class="material-icons color-green">check_circle_outline</i>',
        text: i18n.global.t('common.copied'),
        position: 'center',
        closeTimeout: 2000,
      });
    }
    await toastIcon.value.open();
  }

  function forwardMsg(message) {
    useMessageStore().messageIsSelectedAction(true);
    if (isTablet?.value) {
      f7.$('span .messsages').css({ 'padding-left': '16px' });
      f7.$('.message').css({ 'margin-left': '100px' });
    }
    selectMessage(message);
    setTimeout(() => {
      forwardAllMsg();
    }, 100);
  }

  function messageDeskTopActions(message) {
    if (messageIsSelected?.value && isTablet?.value) {
      selectMessage(message);
    }
  }

  function openMessageReactionPopover(message) {
    const reactPopover = f7.popover.get('.messageReactionPopover');
    if (reactPopover) {
      f7.popover.close('.messageReactionPopover', true);
      if (options.isOpenedReactionPopover) options.isOpenedReactionPopover.value = false;
    } else {
      f7.popover.open('.messageReactionPopover', true);
      if (options.isOpenedReactionPopover) options.isOpenedReactionPopover.value = true;
    }
  }

  async function interactionSelect(emoji) {
    const msg = currentMessage?.value;
    if (!userAccount?.value) {
      await useAccountStore().getUserAccount();
    }
    if (!userAccount?.value) {
      console.error('User account not available for reaction');
      return;
    }
    if (!msg || !msg.id) {
      return;
    }

    const reactions = Array.isArray(msg.reactions) ? [...msg.reactions] : [];
    const existingReaction = reactions.find(
      (r) => r.emojiString === emoji.id
    );
    const existingAuthor = existingReaction?.authors?.find(
      (a) => a.userId === userAccount.value.userId
    );

    if (existingReaction && existingAuthor) {
      const updatedReaction = {
        ...existingReaction,
        count: (existingReaction.count || 1) - 1,
        authors: existingReaction.authors.filter(
          (a) => a.userId !== userAccount.value.userId
        ),
      };
      const reactionIndex = reactions.indexOf(existingReaction);
      if (updatedReaction.count <= 0 || updatedReaction.authors.length === 0) {
        reactions.splice(reactionIndex, 1);
      } else {
        reactions.splice(reactionIndex, 1, updatedReaction);
      }
      msg.reactions = reactions;
      useMessageStore().updateGroupMessage(msg);
      messageSocketClient?.value?.send(
        `/app/message.unreact.${existingReaction.id}.${existingAuthor.id}`
      );
    } else {
      const reactionTypeMap = {
        '+1': 'Like',
        clap: 'Applause',
        heart_hands: 'Support',
        heart: 'Love',
        bulb: 'Insightful',
        laughing: 'Laughing',
      };
      const reaction = {
        emoji: emoji.native,
        emojiString: emoji.id,
        reactionType: reactionTypeMap[emoji.id] || emoji.id,
        users: [userAccount.value.userId],
        message_id: msg.id,
        count: 1,
        authors: [
          {
            userId: userAccount.value.userId,
            name: userAccount.value.name,
            logo: userAccount.value.avatar,
            authorType: 'Person',
          },
        ],
      };
      msg.reactions = [...reactions, reaction];
      useMessageStore().updateGroupMessage(msg);
      messageSocketClient?.value?.send(
        '/app/message.react',
        JSON.stringify(reaction)
      );
    }
    if (messageActionSheet.value) {
      messageActionSheet.value.close();
    }
    f7.popover.close('.messageReactionPopover');
  }

  function showMessageReactions(message, e) {
    if (e?.stopPropagation) e.stopPropagation();
    if (options.reactionMessage) options.reactionMessage.value = message;
    openAdaptiveModal('.showReactionsPopover');
  }

  function currentUsReaction(message, reaction, author) {
    if (author.userId !== userAccount?.value?.userId || !message) {
      return;
    }

    const reactions = Array.isArray(message.reactions)
      ? [...message.reactions]
      : [];
    const reactionIndex = reactions.findIndex((r) => r.id === reaction.id);
    if (reactionIndex < 0) {
      return;
    }

    const updatedReaction = {
      ...reactions[reactionIndex],
      count: (reactions[reactionIndex].count || 1) - 1,
      authors: reactions[reactionIndex].authors.filter(
        (a) => a.userId !== author.userId
      ),
    };
    if (updatedReaction.count <= 0 || updatedReaction.authors.length === 0) {
      reactions.splice(reactionIndex, 1);
    } else {
      reactions.splice(reactionIndex, 1, updatedReaction);
    }
    message.reactions = reactions;
    useMessageStore().updateGroupMessage(message);
    messageSocketClient?.value?.send(
      `/app/message.unreact.${reaction.id}.${author.id}`
    );
  }

  return {
    messageActionSheet,
    groupActionSheet,
    toastIcon,
    transferring,
    reducePageWidth,
    resetPageWidth,
    addParticipantGroupRightPanel,
    openSendMessageLeftPanel,
    attachFileOpenActions,
    opencreateGroupLeftPanel,
    openContactDetailPanel,
    openMsgActionsSheet,
    openMsgActionsPopover,
    openMessageOpenActions,
    groupDetailPopover,
    protectGroup,
    unprotectGroup,
    messageActions,
    replyMsg,
    removeReplyMessage,
    replyMsgPrivately,
    deleteForMe,
    deleteSelectedMsg,
    deleteForAll,
    forwardAllMsg,
    selectRecipientUser,
    deleteRecipientChip,
    transferMessages,
    makeSelectable,
    selectMsg,
    selectMessage,
    clearSelection,
    copyMsg,
    forwardMsg,
    messageDeskTopActions,
    openMessageReactionPopover,
    interactionSelect,
    showMessageReactions,
    currentUsReaction,
  };
}

export function useGetUsers(options = {}) {
  const { users, messageList } = options;

  const autocompleteDropdownAjaxTypeahead = ref(null);

  function updateList() {
    if (options.keycloak?.value?.authenticated && !users?.value) {
      useAccountStore().getAllUsers();
    }
    f7.$('#userRecipientlist').hide();
    f7.$('.searchbar-hide-on-search').hide();
    if (users?.value && users.value.length > 0) {
      getUserList();
    }
  }

  function getUserList() {
    autocompleteDropdownAjaxTypeahead.value = f7.autocomplete.create({
      inputEl: '#searchbar-autocomplete input[type="search"]',
      openIn: 'dropdown',
      preloader: true,
      preloaderColor: 'multi',
      valueProperty: 'name',
      textProperty: 'name',
      limit: 20,
      typeahead: true,
      notFoundText: i18n.global.t('common.nothingFound'),
      dropdownContainerEl: '#autocompleteList',
      renderItem(item) {
        if (item.placeholder) {
          return `<li class=" item-content panel-close" data-panel=".sendMessageLeftPanel">
                      <div class="item-inner">
                        <div class="item-title-row">
                          <div class="item-title">${item.text}</div>
                        </div>
                      </div>
                  </li>`;
        }
        for (const user of users?.value || []) {
          if (user['name'] === item['value']) {
            f7.utils.extend(item, user);
            break;
          }
        }
        const initials = item.name
          ? (item.name.split(' ')[0] || ' ').charAt(0) +
            '' +
            (item.name.split(' ')[1] || ' ').charAt(0)
          : '';
        let pict;
        if (item.logo === null) {
          pict = `<span v-else color="auto" class="badge" slot="media" style="height: 48px; width: 48px; font-size: 16px; border-radius: 25px; background-color:var(--f7-theme-color) ">${initials}</span>`;
        } else {
          pict = `<img src="${item.logo}" class="userImage" v-if="${item.logo}" width="40"    style="height: 48px; width: 48px; border-radius: 25px" slot="media"/>`;
        }

        return `<div class="list media-list   panel-close" data-panel=".sendMessageLeftPanel">
                      <ul class="padding-left no-margin-left">
                        <li class="no-margin-left no-padding-left">
                        <label class="item-radio item-content  no-margin-left no-padding-left" data-value="${item.value}"   @click="selectUser(${item})">
                            <div class="item-media">${pict}</div>
                            <div class="item-inner">
                                <div class="item-title">${item.text}</div>
                              <div class="item-footer avocash">${item.tag || ' '}</div>
                            </div>
                          </label>
                        </li>
                      </ul>
                    </div>`;
      },
      source(query, render) {
        const autocomplete = this;
        const results = [];
        if (query.length === 0) {
          render(results);
          return;
        }
        autocomplete.preloaderShow();
        for (const user of users?.value || []) {
          if (
            user.name.toLowerCase().includes(query.toLowerCase()) ||
            user.phoneNumber.includes(query.toLowerCase()) ||
            user.email
              .substring(0, user.email.indexOf('@'))
              .toLowerCase()
              .includes(query.toLowerCase()) ||
            user.username.toLowerCase().includes(query.toLowerCase()) ||
            user.tag.toLowerCase().includes(query.toLowerCase()) ||
            user.accountNumber.includes(query.toLowerCase())
          )
            results.push(user);
        }
        autocomplete.preloaderHide();
        render(results);
      },
      on: {
        change(value) {
          f7.preloader.show('multi');
          useAccountStore().setUser(value[0]);
          useMessageStore().getCounterPartUser(value[0].userId);
          if ((messageList?.value?.length || 0) >= 1) {
            useMessageStore().getUserGroupMessages('');
            const msgStore = useMessageStore();
            msgStore.messageList = [];
            msgStore.messagesData = [];
          }
          setTimeout(() => {
            f7.views.main.router.navigate(
              `/message/${value[0].userId}/${value[0].groupType}`,
              {
                reloadDetail: false,
              }
            );
            f7.preloader.hide();
            f7.panel.close('.sendMessageLeftPanel');
          }, 2000);
        },
      },
    });
  }

  function selectUser(user, chatUsers, chatUsersToPost) {
    const userIndexed = _.find(
      chatUsers.value,
      (userx) => userx.id === user.id
    );
    if (userIndexed) {
      deleteChip(user, chatUsers, chatUsersToPost);
      return;
    }
    const currentUser = { userId: user.userId };
    chatUsers.value.push(user);
    chatUsersToPost.value.push(currentUser);
  }

  function selectChatUser(user) {
    if (options.selectuser) options.selectuser(user);
  }

  function deleteChip(chatUser, chatUsers, chatUsersToPost) {
    const index = chatUsers.value.indexOf(chatUser);
    chatUsers.value.splice(index, 1)[0];
    chatUsersToPost.value.splice(index, 1)[0];
  }

  function clearSearch() {
    f7.$('.searchbar-hide-on-search').show();
    f7.$('#userRecipientlist').show();
  }

  function getPicturesAvatar() {
    if (options.fileInput) options.fileInput.value = 'chatAvatarInput';
    if (options.avatar) options.avatar.value = true;
    if (options.getFiles) options.getFiles();
  }

  function openSaveGroupLeftPanel() {
    f7.panel.open('.saveGroupLeftPanel');
  }

  onMounted(() => {
    if (options.keycloak?.value?.authenticated && users?.value === '') {
      useAccountStore().getAllUsers();
    }
    if (f7.$('#autocomplete-dropdown-ajax-typeahead').val() === '') {
      useAccountStore().setUser('');
    }
    getUserList();

    const root = document.documentElement;
    if (f7.width >= 768) {
      root.style.setProperty('--f7-page-master-width', '418px');
      root.style.setProperty('--f7-panel-shadow', 'transparent');
      root.style.setProperty('--f7-panel-width', '418px');
      return;
    }
    root.style.setProperty('--f7-panel-width', '100%');
  });

  return {
    autocompleteDropdownAjaxTypeahead,
    updateList,
    getUserList,
    selectUser,
    selectChatUser,
    deleteChip,
    clearSearch,
    getPicturesAvatar,
    openSaveGroupLeftPanel,
  };
}

export function useMessageGroup(options = {}) {
  const {
    isTablet,
    chatGroupParticipantList,
    newGroupParticipants,
    participantsToAdd,
    group,
    groupName,
    description,
    groupNameDisabled,
    descDisabled,
    chatUsers,
    chatUsersToPost,
    documentsUrl,
    avatarDocument,
  } = options;

  function addCurrentParticipants() {
    for (const participant of chatGroupParticipantList?.value || []) {
      if (isTablet?.value === false) {
        f7.$(`.newGroupParticipant${participant.userId}`).css({
          'background-color': '#87bfff',
          disabled: true,
        });
      }
      if (isTablet?.value === true) {
        f7.$(`input[name=participant${participant.userId}]`).prop({
          checked: true,
          disabled: true,
        });
      }
    }
  }

  function selectParticipant(user) {
    const userId = user.userId;
    const isTabletVal = isTablet?.value === true;
    const userIndex = (newGroupParticipants?.value || []).findIndex(
      (x) => x.userId === userId
    );
    const isInGroup = (chatGroupParticipantList?.value || []).some(
      (x) => x.userId === userId
    );

    if (userIndex >= 0 && !isInGroup) {
      if (isTabletVal) {
        f7.$(`input[name=participant${userId}]`).prop('checked', false);
      } else {
        f7.$(`.newGroupParticipant${userId}`).css({
          'background-color': 'transparent',
        });
      }
      newGroupParticipants.value.splice(userIndex, 1);
      return;
    }

    if (userIndex >= 0 || isInGroup) {
      return;
    }

    newGroupParticipants.value.push(user);
    participantsToAdd.value.push({ userId });
    if (isTabletVal) {
      f7.$(`input[name=participant${userId}]`).prop('checked', true);
    } else {
      f7.$(`.newGroupParticipant${userId}`).css({
        'background-color': '#87bfff',
      });
    }
  }

  function deleteParticioantChip(grpParticipant) {
    const index = (newGroupParticipants?.value || []).indexOf(grpParticipant);
    const index2 = (participantsToAdd?.value || []).findIndex(
      (x) => x.userId === grpParticipant.userId
    );

    if (
      (chatGroupParticipantList?.value || []).some(
        (x) => x.userId === grpParticipant.userId
      )
    ) {
      return;
    }

    if (index > -1) newGroupParticipants.value.splice(index, 1);
    if (index2 > -1) participantsToAdd.value.splice(index2, 1);

    if (isTablet?.value) {
      f7.$(`input[name=participant${grpParticipant.userId}]`).prop(
        'checked',
        false
      );
    } else {
      f7.$(`.newGroupParticipant${grpParticipant.userId}`).css({
        'background-color': 'transparent',
      });
    }
  }

  async function addNewParticipants() {
    if (
      !Array.isArray(participantsToAdd?.value) ||
      participantsToAdd.value.length === 0
    )
      return;

    try {
      await useMessageStore().addGroupParticipant({
        chatUsers: participantsToAdd.value,
        groupId: group.value.id,
      });

      (newGroupParticipants?.value || []).slice().forEach((participant) => {
        deleteParticioantChip(participant);
      });

      (chatGroupParticipantList?.value || []).forEach((participant) => {
        if (isTablet?.value) {
          f7.$(`input[name=participant${participant.userId}]`).prop({
            checked: false,
            disabled: false,
          });
        } else {
          f7.$(`.newGroupParticipant${participant.userId}`).css({
            'background-color': 'transparent',
            disabled: false,
          });
        }
      });

      f7.panel.close('.addParticipantGroupRightPanel', true);
    } catch (err) {
      console.error('Failed to add participants:', err);
    }
  }

  function removeParticipant(participant) {
    f7.dialog.confirm(
      i18n.global.t('tamtammessage.areYouSureToRemoveParticipant', {
        participantToDelete: `${participant.name}`,
      }),
      () => {
        useMessageStore().removeGroupParticipant({
          userId: participant.userId,
          groupId: group.value.id,
        });
      }
    );
  }

  function grantAdminRole(participant) {
    f7.dialog.confirm(
      i18n.global.t('tamtammessage.areYouSureToGiveAdminright', {
        participantToDelete: `${participant.name}`,
      }),
      () => {
        useMessageStore().grantGroupAdminRights({
          userId: participant.userId,
          groupId: group.value.id,
        });
      }
    );
  }

  function withdrawAdminRole(participant) {
    f7.dialog.confirm(
      i18n.global.t('tamtammessage.areYouSureToRemoveAdminright', {
        participantToDelete: `${participant.name}`,
      }),
      () => {
        useMessageStore().removeGroupAdminRights({
          userId: participant.userId,
          groupId: group.value.id,
        });
      }
    );
  }

  function leaveGroup(groupItem) {
    f7.dialog.confirm(
      i18n.global.t('tamtammessage.areYouSureToExitGroup', {
        theGroup: `${groupItem.name}`,
      }),
      () => {
        useMessageStore().leaveGroup({
          groupId: groupItem.id,
        });
      }
    );
  }

  function deleteGroup(groupItem) {
    f7.dialog.confirm(
      i18n.global.t('tamtammessage.areYouSureToDeletetGroup', {
        theGroup: `${groupItem.name}`,
      }),
      () => {
        useMessageStore().deleteGroup({
          groupId: groupItem.id,
        });
      }
    );
  }

  function deleteChat(groupItem) {
    f7.dialog.confirm(
      i18n.global.t('tamtammessage.areYouSureToDeletetChat', {
        theGroup: `${groupItem.name}`,
      }),
      () => {
        useMessageStore().deleteGroup({
          groupId: groupItem.id,
        });
      }
    );
  }

  function editGroupName() {
    groupNameDisabled.value = false;
    f7.input.focus('#nameInput');
  }

  function saveGroupName() {
    useMessageStore().updateGroupInfo({
      changeType: 'groupName',
      change: groupName.value,
      groupId: group.value.id,
    });
    groupNameDisabled.value = true;
    f7.input.blur('#nameInput');
  }

  function editDescription() {
    descDisabled.value = false;
    f7.input.focus('#descInput');
  }

  function saveDesc() {
    useMessageStore().updateGroupInfo({
      changeType: 'description',
      change: description.value,
      groupId: group.value.id,
    });
    descDisabled.value = true;
    f7.input.blur('#descInput');
  }

  function getPicturesAvatar() {
    if (options.fileInput) options.fileInput.value = 'chatAvatarInput';
    if (options.avatar) options.avatar.value = true;
    if (options.getFiles) options.getFiles();
  }

  function saveAvatar() {
    const result = options.cropper?.value?.getResult();
    if (result) {
      result.canvas.toBlob((blob) => {
        const forms = new FormData();
        forms.append('file', blob, 'avatar');
        forms.append('groupId', group.value.id);
        useMessageStore().updateGroupAvatar({
          forms,
        });
      });
      return;
    }
    const forms = new FormData();
    for (const file of options.files?.value || []) {
      forms.append('file', file, 'avatar');
    }
    forms.append('groupId', group.value.id);
    useMessageStore().updateGroupAvatar({
      forms,
    });
  }

  function blockUser(user) {
    f7.dialog.confirm(
      i18n.global.t('tamtammessage.areYouSureToBlock', {
        theParticipant: `${user.name}`,
      }),
      () => {
        useMessageStore().blockUser({ chatUserId: user.id });
      }
    );
  }

  function unBlockUser(user) {
    useMessageStore().unBlockUser({ chatUserId: user.id });
  }

  function markUnread(currentGroupDetail) {
    useMessageStore().setUnReadMsgCount({
      id: currentGroupDetail.id,
      count: 1,
    });
  }

  /**
   * Uploads the picked avatar and returns its public URL.
   *
   * The upload has to finish before the group is created: the document is
   * replaced by its uploaded version (new id, CDN url) and the group is
   * created with whatever the document looks like at that moment.
   */
  async function uploadGroupAvatar() {
    const draft = avatarDocument?.value || (documentsUrl?.value || [])[0];
    if (!draft) return null;
    await useAuthStore().postR2Document({
      bucketName: import.meta.env.VITE_R2_PUBLIC_BUCKET_NAME,
      fileId: draft.fileId,
    });
    const uploaded = (documentsUrl?.value || []).find(
      (doc) => doc.fileName === draft.fileName
    );
    return uploaded?.url || null;
  }

  async function createGroup(user) {
    let groupParticipantList = [];
    let groupType = null;
    let avatar = null;
    if (user) {
      groupParticipantList = [{ userId: user.userId }];
      groupType = 'PERSONAL';
    } else {
      groupType = 'GROUP';
      groupParticipantList = (chatUsers?.value || []).map((u) => ({
        userId: u.userId,
      }));
      avatar = await uploadGroupAvatar();
    }

    const chat_group = {
      name: user ? null : groupName.value,
      description: user ? null : description.value,
      groupParticipantList,
      groupType,
      avatar,
    };
    return useMessageStore().createGroup(chat_group);
  }

  async function createSubGroup(groupItem) {
    const subGroup = {
      name: groupName.value,
      description: description.value,
      groupParticipantList: (chatUsers?.value || []).map((u) => ({
        userId: u.userId,
      })),
      groupType: 'SUBGROUP',
      avatar: await uploadGroupAvatar(),
    };
    return useMessageStore().createSubGroup({
      subGroup,
      groupId: groupItem.id,
    });
  }

  /**
   * Starts the sub-group flow for a group the user administers.
   *
   * The member list is loaded by the creation panel itself, so this only has to
   * point the flow at the parent group and hand over the panel.
   */
  function addSubgroup(currentGroupDetail) {
    useGroupCreation().openGroupCreation(currentGroupDetail);
  }

  const chatUserState = computed(() => useMessageStore().chatUser);
  const messageState = computed(() => useMessageStore().message);
  const chatUserListState = computed(() => useMessageStore().chatUserList);
  const messageListState = computed(() => useMessageStore().messageList);
  const groupState = computed(() => useMessageStore().group);
  const groupListState = computed(() => useMessageStore().groupList);
  const subGroupState = computed(() => useMessageStore().subGroup);
  const subGroupListState = computed(() => useMessageStore().subGroupList);
  const blockedUserState = computed(() => useMessageStore().blockedUser);
  const blockedUserListState = computed(() => useMessageStore().blockedUserList);
  const blockedUserMsgState = computed(() => useMessageStore().blockedUserMsg);
  const groupAdminState = computed(() => useMessageStore().groupAdmin);
  const groupAdminListState = computed(() => useMessageStore().groupAdminList);
  const counterPartUserState = computed(() => useMessageStore().counterPartUser);
  const counterPartUserListState = computed(() => useMessageStore().counterPartUserList);
  const groupParticipantListState = computed(() => useMessageStore().groupParticipantList);
  const messageIsSelectedState = computed(() => useMessageStore().messageIsSelected);
  const selectedMessagesState = computed(() => useMessageStore().selectedMessages);
  const messageSocketClientState = computed(() => useMessageStore().messageSocketClient);
  const msgCltConnectedState = computed(() => useMessageStore().msgCltConnected);

  const authStore = useAuthStore();
  const phoneNumberDetailState = computed(() => authStore.phoneNumberDetail);
  const mobilePhoneErrorState = computed(() => authStore.mobilePhoneError);
  const profileState = computed(() => authStore.profile);
  const isDeskTopState = useDesktopScreen();
  const isTabletState = useTabletScreen().matches;
  const isNativePlatformState = computed(() => authStore.isNativePlatform);
  const localeState = computed(() => authStore.locale);
  const documentsUrlState = computed(() => authStore.documentsUrl);

  const accountStore = useAccountStore();
  const primaryAccountState = computed(() => accountStore.primaryAccount);
  const businessAccountState = computed(() => accountStore.businessAccount);
  const userDetailState = computed(() => accountStore.userDetail);
  const searchedUserState = computed(() => accountStore.searchedUser);

  return {
    addCurrentParticipants,
    selectParticipant,
    deleteParticioantChip,
    addNewParticipants,
    removeParticipant,
    grantAdminRole,
    withdrawAdminRole,
    leaveGroup,
    deleteGroup,
    deleteChat,
    editGroupName,
    saveGroupName,
    editDescription,
    saveDesc,
    getPicturesAvatar,
    saveAvatar,
    blockUser,
    unBlockUser,
    markUnread,
    createGroup,
    createSubGroup,
    addSubgroup,
    chatUser: chatUserState,
    message: messageState,
    chatUserList: chatUserListState,
    messageList: messageListState,
    group: groupState,
    groupList: groupListState,
    subGroup: subGroupState,
    subGroupList: subGroupListState,
    blockedUser: blockedUserState,
    blockedUserList: blockedUserListState,
    blockedUserMsg: blockedUserMsgState,
    groupAdmin: groupAdminState,
    groupAdminList: groupAdminListState,
    counterPartUser: counterPartUserState,
    counterPartUserList: counterPartUserListState,
    groupParticipantList: groupParticipantListState,
    messageIsSelected: messageIsSelectedState,
    selectedMessages: selectedMessagesState,
    messageSocketClient: messageSocketClientState,
    msgCltConnected: msgCltConnectedState,
    phoneNumberDetail: phoneNumberDetailState,
    mobilePhoneError: mobilePhoneErrorState,
    profile: profileState,
    isDeskTop: isDeskTopState,
    isTablet: isTabletState,
    isNativePlatform: isNativePlatformState,
    locale: localeState,
    documentsUrl: documentsUrlState,
    primaryAccount: primaryAccountState,
    businessAccount: businessAccountState,
    userDetail: userDetailState,
    searchedUser: searchedUserState,
  };
}

export function useMessage(opts = {}) {
  const optionsResult = useMessageOptions(opts);
  const groupResult = useMessageGroup(opts);
  return { ...optionsResult, ...groupResult };
}
